import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { initAnalytics, trackPageView, track } from "@/lib/analytics";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";

import LoadingScreen from "@/components/LoadingScreen";
import SEOHead from "@/components/SEOHead";
import ServicesSchema from "@/components/ServicesSchema";
import MemorialDayBanner from "@/components/MemorialDayBanner";

// Defer non-critical chrome — loads after first paint, removes ~250KB from main bundle
const FloatingCTA = lazy(() => import("@/components/FloatingCTA"));
const ChatWidget = lazy(() => import("@/components/ChatWidget"));
const MobileEstimateBar = lazy(() => import("@/components/MobileEstimateBar"));
const ExitIntent = lazy(() => import("@/components/ExitIntent"));
const LanguageSelector = lazy(() => import("@/components/LanguageSelector"));
const DiscountsDrawer = lazy(() => import("@/components/DiscountsDrawer"));
const BackToTop = lazy(() => import("@/components/BackToTop"));
const StickyQuoteTab = lazy(() => import("@/components/StickyQuoteTab"));

// Route-split: each page lazy-loaded so Stripe/heavy deps stay out of the initial bundle
import HomePage from "@/pages/HomePage"; // keep eager for fastest LCP on /
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const ServiceCityPage = lazy(() => import("@/pages/ServiceCityPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const GalleryPage = lazy(() => import("@/pages/GalleryPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const BookPage = lazy(() => import("@/pages/BookPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const DealershipFleetPage = lazy(() => import("@/pages/DealershipFleetPage"));
const EstimatePage = lazy(() => import("@/pages/EstimatePage"));
const ThankYouPage = lazy(() => import("@/pages/ThankYouPage"));
const PlansPage = lazy(() => import("@/pages/PlansPage"));
const CheckoutReturn = lazy(() => import("@/pages/CheckoutReturn"));
const BookingReturn = lazy(() => import("@/pages/BookingReturn"));
const BookingStatusPage = lazy(() => import("@/pages/BookingStatusPage"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const LocationsPage = lazy(() => import("@/pages/LocationsPage"));
const CityAliasRedirect = lazy(() => import("@/pages/CityAliasRedirect"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const DashboardLayout = lazy(() => import("@/components/dashboard/DashboardLayout"));
const CustomerOverview = lazy(() => import("@/pages/dashboard/CustomerOverview"));
const MyBookings = lazy(() => import("@/pages/dashboard/MyBookings"));
const MyVehicles = lazy(() => import("@/pages/dashboard/MyVehicles"));
const Rewards = lazy(() => import("@/pages/dashboard/Rewards"));
const Profile = lazy(() => import("@/pages/dashboard/Profile"));
const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminBookings = lazy(() => import("@/pages/admin/AdminBookings"));
const AdminCustomers = lazy(() => import("@/pages/admin/AdminCustomers"));
const AdminLeads = lazy(() => import("@/pages/admin/AdminLeads"));
const AdminProofs = lazy(() => import("@/pages/admin/AdminProofs"));
const AdminDiscounts = lazy(() => import("@/pages/admin/AdminDiscounts"));
const AdminDiscountAnalytics = lazy(() => import("@/pages/admin/AdminDiscountAnalytics"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminInvitePast = lazy(() => import("@/pages/admin/AdminInvitePast"));

import { RewardsProvider } from "@/components/RewardsProvider";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

const queryClient = new QueryClient();

const PUBLIC_ROUTE_CHROME = true;

const PublicShell = ({ children }: { children: React.ReactNode }) => (
  <RewardsProvider>
    <ServicesSchema />
    <LoadingScreen />
    <MemorialDayBanner />
    <Navbar />
    {children}
    <Footer />
    <Suspense fallback={null}>
      <FloatingCTA />
      <MobileEstimateBar />
      <ExitIntent />
      <LanguageSelector />
      <ChatWidget />
      <DiscountsDrawer />
      <BackToTop />
      <StickyQuoteTab />
    </Suspense>
  </RewardsProvider>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  const path = location.pathname;
  const isAppRoute = path.startsWith("/dashboard") || path.startsWith("/admin") || path === "/auth";

  // Initialize GA4 once and fire page_view on every route change.
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    // Wait one tick so document.title reflects the new route.
    const id = window.setTimeout(() => trackPageView(path + location.search), 50);
    return () => window.clearTimeout(id);
  }, [path, location.search]);

  // Delegated click tracking for tel: links anywhere in the app.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const a = target?.closest?.("a[href^='tel:']") as HTMLAnchorElement | null;
      if (a) {
        track("phone_clicked", {
          phone: a.getAttribute("href")?.replace(/^tel:/, "") ?? "",
          location: window.location.pathname,
        });
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return (
    <>
      <SEOHead />
      <AnimatePresence mode="wait">
        <Suspense fallback={null}>
          <Routes location={location} key={location.pathname}>
            {/* Auth */}
            <Route path="/auth" element={<AuthPage />} />

            {/* Customer dashboard */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<CustomerOverview />} />
              <Route path="bookings" element={<MyBookings />} />
              <Route path="vehicles" element={<MyVehicles />} />
              <Route path="rewards" element={<Rewards />} />
              <Route path="profile" element={<Profile />} />
            </Route>

            {/* Admin dashboard */}
            <Route path="/admin" element={<ProtectedRoute requireAdmin><DashboardLayout admin /></ProtectedRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="proofs" element={<AdminProofs />} />
              <Route path="discounts" element={<AdminDiscounts />} />
              <Route path="discount-analytics" element={<AdminDiscountAnalytics />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="invite-past" element={<AdminInvitePast />} />
            </Route>

            {/* Public site (wrapped in shell) */}
            <Route path="/" element={<PublicShell><HomePage /></PublicShell>} />
            <Route path="/services" element={<PublicShell><ServicesPage /></PublicShell>} />
            <Route path="/services/:serviceSlug" element={<PublicShell><ServicePage /></PublicShell>} />
            <Route path="/services/:serviceSlug/:citySlug" element={<PublicShell><ServiceCityPage /></PublicShell>} />
            <Route path="/pricing" element={<PublicShell><PricingPage /></PublicShell>} />
            <Route path="/gallery" element={<PublicShell><GalleryPage /></PublicShell>} />
            <Route path="/about" element={<PublicShell><AboutPage /></PublicShell>} />
            <Route path="/reviews" element={<PublicShell><ReviewsPage /></PublicShell>} />
            <Route path="/book" element={<PublicShell><BookPage /></PublicShell>} />
            <Route path="/book/return" element={<PublicShell><BookingReturn /></PublicShell>} />
            <Route path="/booking/status/:bookingId" element={<PublicShell><BookingStatusPage /></PublicShell>} />
            <Route path="/blog" element={<PublicShell><BlogPage /></PublicShell>} />
            <Route path="/blog/:slug" element={<PublicShell><BlogPostPage /></PublicShell>} />
            <Route path="/terms" element={<PublicShell><TermsPage /></PublicShell>} />
            <Route path="/dealership-fleet" element={<PublicShell><DealershipFleetPage /></PublicShell>} />
            <Route path="/estimate" element={<PublicShell><EstimatePage /></PublicShell>} />
            <Route path="/plans" element={<PublicShell><PlansPage /></PublicShell>} />
            <Route path="/checkout/return" element={<PublicShell><CheckoutReturn /></PublicShell>} />
            <Route path="/thank-you" element={<PublicShell><ThankYouPage /></PublicShell>} />
            <Route path="/thanks" element={<PublicShell><ThankYouPage /></PublicShell>} />
            <Route path="/success" element={<PublicShell><ThankYouPage /></PublicShell>} />
            <Route path="/form-success" element={<PublicShell><ThankYouPage /></PublicShell>} />
            <Route path="/locations" element={<PublicShell><LocationsPage /></PublicShell>} />
            <Route path="/mobile-detailing/:citySlug" element={<PublicShell><CityPage /></PublicShell>} />
            {["fairfax-car-detailing","tysons-car-detailing","mclean-car-detailing","arlington-car-detailing","vienna-car-detailing","bethesda-car-detailing","great-falls-car-detailing"].map((alias) => (
              <Route key={alias} path={`/${alias}`} element={<CityAliasRedirect />} />
            ))}
            {/* Common 404-prone URL redirects → resolve cleanly to live pages */}
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/index" element={<Navigate to="/" replace />} />
            <Route path="/index.html" element={<Navigate to="/" replace />} />
            <Route path="/service-areas" element={<Navigate to="/locations" replace />} />
            <Route path="/areas" element={<Navigate to="/locations" replace />} />
            <Route path="/contact" element={<Navigate to="/about" replace />} />
            <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
          </Routes>
        </Suspense>
      </AnimatePresence>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <SonnerToaster />
      <BrowserRouter>
        <AuthProvider>
          <AnimatedRoutes />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
